import { AxiosInstance } from 'axios';
import { MutableRefObject, useCallback } from 'react';
import { decode } from '@msgpack/msgpack';

export interface InductionRequest {
  getStations: (country: string) => Promise<any>;
  getStationDetails: (station: string) => Promise<any>;
  getCoverageStation: (lat: string, lng: string) => Promise<any>;
  getCoverage: (lat: string, lng: string, country: string) => Promise<any>;
  getContainer: (containerId: string) => Promise<any>;
  getContainerPdf: (containerIds: string[]) => Promise<any>;
  getContainersDynamic: (country: string, station: string) => Promise<any>;
  removeFormContainer: (station: string, country: string, container: string, orderCounter: string) => Promise<any>;
  addToContainer: (station: string, country: string, orderId: string, containerId?: string) => Promise<any>;
  emptyContainer: (containerId: string, station: string, country: string, source?: string) => Promise<any>;
  addToContainerLastMile: (variables: any) => Promise<any>;
  toggleContainer: (containerId: string, status: boolean) => Promise<any>;
  induct: (variables: any) => Promise<any>;
  lmToMMContainer: (destStation: string, container_id: string) => Promise<any>;
  queryContainersRest: (country: string, station: string, withPdf: boolean) => Promise<any>;
}

export default function useInductionRequest(
  httpInstance: MutableRefObject<AxiosInstance | null>,
): InductionRequest {
  const getStations = useCallback(
    async (country: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query STATIONS_QUERY($country: String!) {
          stations(country: $country)
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { country },
      });
      return data.data.stations;
    },
    [httpInstance],
  );

  const getStationDetails = useCallback(
    async (station: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query STATIONS_QUERY($station: String!) {
          stationDetails(name: $station) {
            station
            location {
              latitude
              longitude
            }
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { station },
      });
      return data.data.stationDetails;
    },
    [httpInstance],
  );

  const getCoverageStation = useCallback(
    async (lat: string, lng: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query LOCATION_QUERY($lat: String, $lng: String) {
          coverageStation(latitude: $lat, longitude: $lng) {
            coveragestation
            country
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { lat, lng },
      });
      return data.data.coverageStation;
    },
    [httpInstance],
  );

  const getCoverage = useCallback(
    async (lat: string, lng: string, country: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query LOCATION_QUERY($lat: String, $lng: String, $country: String!) {
          coverage(latitude: $lat, longitude: $lng, country: $country) {
            coverageStation
            coverageZone
            country
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { lat, lng, country },
      });
      return data.data.coverage;
    },
    [httpInstance],
  );

  const getContainer = useCallback(
    async (containerId: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query Query($containerId: String!) {
          container(containerId: $containerId) {
            status
            station
            orders {
              counter
              country
            }
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { containerId },
      });
      return data.data.container;
    },
    [httpInstance],
  );

  const getContainerPdf = useCallback(
    async (containerIds: string[]) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query Query($containerIds: [String!]) {
          containers(containerIds: $containerIds) {
            pdfUrl
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { containerIds },
      });
      return data.data.containers;
    },
    [httpInstance],
  );

  const getContainersDynamic = useCallback(
    async (country: string, station: string) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const query = `
        query Query($country: String!, $station: String!) {
          containersDynamic(
            filter: { country: $country, station: $station, type: LAST_MILE_VIRTUAL }
          ) {
            containers {
              id
              tag
              status
              orders {
                counter
                }
            }
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query,
        variables: { country, station },
      });
      return data.data.containersDynamic;
    },
    [httpInstance],
  );

  const removeFormContainer = useCallback(
    async (
      station: string,
      country: string,
      container: string,
      orderCounter: string,
    ) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation Mutation(
          $station: String!
          $country: String!
          $container: String!
          $orderCounter: String!
        ) {
          removeFromContainer(
            station: $station
            country: $country
            container: $container
            orderCounter: $orderCounter
          ) {
            finger
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables: { station, country, container, orderCounter },
      });
      return data.data.removeFromContainer;
    },
    [httpInstance],
  );

  const addToContainer = useCallback(
    async (
      station: string,
      country: string,
      orderId: string,
      containerId?: string,
    ) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation Mutation(
          $station: String!
          $country: String!
          $orderId: String!
          $containerId: String
        ) {
          addToContainer(
            station: $station
            country: $country
            orderId: $orderId
            containerId: $containerId
          ) {
            finger
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables: { station, country, orderId, containerId },
      });
      return data.data.addToContainer;
    },
    [httpInstance],
  );

  const emptyContainer = useCallback(
    async (
      containerId: string,
      station: string,
      country: string,
      source?: string,
    ) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation Mutation(
          $containerId: String!
          $station: String!
          $country: String!
          $source: String
        ) {
          emptyContainer(
            containerId: $containerId
            station: $station
            country: $country
            source: $source
          ) {
            id
            tag
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables: { containerId, station, country, source },
      });
      return data.data.emptyContainer;
    },
    [httpInstance],
  );

  const addToContainerLastMile = useCallback(
    async (variables: any) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation Mutation(
          $station: String!
          $country: String!
          $orderId: String!
          $containerId: String
          $source: String
          $routeId: String!
          $routeStop: Int!
          $routePackage: Int!
          $idempotenceGroup: String!
          $fixed: Boolean!
        ) {
          addToContainerLastMile(
            station: $station
            country: $country
            orderId: $orderId
            containerId: $containerId
            source: $source
            routeId: $routeId
            routeStop: $routeStop
            routePackage: $routePackage
            idempotenceGroup: $idempotenceGroup
            fixed: $fixed
          ) {
            station
            order {
              identifier
              currentStation
            }
            status
            containerid
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables,
      });
      return data.data.addToContainerLastMile;
    },
    [httpInstance],
  );

  const toggleContainer = useCallback(
    async (containerId: string, status: boolean) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation Mutation($containerId: String!, $status: Boolean) {
          toggleContainer(containerId: $containerId, active: $status) {
            status
          }
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables: { containerId, status },
      });
      return data.data.toggleContainer;
    },
    [httpInstance],
  );

  const induct = useCallback(
    async (variables: any) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation induct(
          $user: String!
          $station: String
          $country: String
          $tracking_id: String!
        ) {
          induct(
            orderIdentifier: $tracking_id
            station: $station
            country: $country
            user: $user
          ) {
            status
            order {
              counter
            }
          }
        }
      `;
      const { data } = await httpInstance.current.post('', { query: mutation, variables });
      return data.data.induct;
    },
    [httpInstance],
  );

  const lmToMMContainer = useCallback(
    async (
      destStation: string,
      container_id: string,
    ) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const mutation = `
        mutation LmToMMContainer($destStation: String!, $container_id: String!) {
          lmToMMContainer(container_id: $container_id, destStation: $destStation)
        }
      `;
      const { data } = await httpInstance.current.post('', {
        query: mutation,
        variables: { destStation, container_id },
      });
      return data.data.lmToMMContainer;
    },
    [httpInstance],
  );

  const queryContainersRest = useCallback(
    async (
      country: string,
      station: string,
      withPdf: boolean,
    ) => {
      if (!httpInstance.current) throw new Error('Http instance is not initialized');
      const result = await httpInstance.current.post(
        '/v1/containersDynamic',
        {
          country,
          station,
          type: 'LAST_MILE_VIRTUAL',
          withPdf,
        },
        {
          headers: {
            Accept: 'application/x-msgpack',
          },
          responseType: 'arraybuffer',
          transformResponse: (data) => decode(data),
        },
      );
      return result.data;
    },
    [httpInstance],
  );

  return {
    getStations,
    getStationDetails,
    getCoverageStation,
    getCoverage,
    getContainer,
    getContainerPdf,
    getContainersDynamic,
    removeFormContainer,
    addToContainer,
    emptyContainer,
    addToContainerLastMile,
    toggleContainer,
    induct,
    lmToMMContainer,
    queryContainersRest,
  };
}
